import { EventSourcePolyfill } from 'event-source-polyfill';

// EventSource-compatible wrapper around `event-source-polyfill` that:
//   * Calls `getHeaders()` on every (re)connect so that expiring auth tokens
//     are refreshed instead of being reused until they 401.
//   * Raises the default heartbeat timeout so normal server-side idle periods
//     don't trigger noisy aborts and reconnect storms.
//   * Rebuilds the underlying EventSource (with a fresh token) when the
//     polyfill permanently gives up (e.g. after a 401) using capped backoff.
//
// The surface mirrors the WHATWG EventSource API enough to be a drop-in
// replacement for our existing `new EventSourcePolyfill(url, { headers })`
// call sites: `addEventListener`, `removeEventListener`, `onopen`,
// `onmessage`, `onerror`, `readyState`, and `close` are all supported.

const DEFAULT_HEARTBEAT_TIMEOUT_MS = 120000;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 15000;

const CONNECTING = 0;
const CLOSED = 2;

export class ReconnectingEventSource {
    constructor(url, { getHeaders, heartbeatTimeout = DEFAULT_HEARTBEAT_TIMEOUT_MS } = {}) {
        if (typeof getHeaders !== 'function') {
            throw new TypeError('ReconnectingEventSource requires a getHeaders() callback.');
        }
        this._url = url;
        this._getHeaders = getHeaders;
        this._heartbeatTimeout = heartbeatTimeout;
        this._listeners = new Map();
        this._onopen = null;
        this._onmessage = null;
        this._onerror = null;
        this._es = null;
        this._closed = false;
        this._reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
        this._reconnectTimer = null;
        // A 401 may just mean the token expired, so allow one reconnect (with a
        // freshly-minted token via getHeaders) before treating it as terminal.
        this._authRetryUsed = false;
        this._connect();
    }

    async _connect() {
        if (this._closed) return;
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }

        let headers;
        try {
            headers = await this._getHeaders();
        } catch (err) {
            if (this._closed) return;
            console.error('ReconnectingEventSource: failed to obtain headers', err);
            this._scheduleReconnect();
            return;
        }
        if (this._closed) return;

        const es = new EventSourcePolyfill(this._url, {
            headers,
            heartbeatTimeout: this._heartbeatTimeout,
        });
        this._es = es;

        // Re-attach all previously-registered listeners to the new instance.
        for (const [type, handlers] of this._listeners) {
            for (const handler of handlers) {
                es.addEventListener(type, handler);
            }
        }
        if (this._onopen) es.onopen = this._onopen;
        if (this._onmessage) es.onmessage = this._onmessage;

        es.addEventListener('open', () => {
            this._reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
            this._authRetryUsed = false;
        });

        es.addEventListener('error', (evt) => {
            if (this._closed) return;
            // The polyfill transitions to CLOSED only when it has given up
            // (e.g. after a non-200 status). Transient errors keep the instance
            // in CONNECTING so its own backoff handles it.
            if (es.readyState === CLOSED) {
                try { es.close(); } catch (_) { /* noop */ }
                if (this._es === es) this._es = null;

                // The polyfill puts the HTTP status on the error event. Honour
                // terminal statuses instead of reconnecting forever: a 403/404
                // will never succeed, and a 401 gets exactly one token-refresh
                // retry before we give up.
                const status = evt && evt.status;
                if (status === 403 || status === 404) {
                    this._giveUp();
                } else if (status === 401) {
                    if (this._authRetryUsed) {
                        this._giveUp();
                    } else {
                        this._authRetryUsed = true;
                        this._scheduleReconnect();
                    }
                } else {
                    this._scheduleReconnect();
                }
            }
            if (this._onerror) {
                try { this._onerror(evt); } catch (handlerErr) {
                    console.error('ReconnectingEventSource onerror handler threw', handlerErr);
                }
            }
        });
    }

    // Permanently stop after a terminal status. Unlike close(), this is driven
    // by the server's response rather than the consumer, but the end state is
    // the same: no further reconnect attempts.
    _giveUp() {
        this._closed = true;
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
    }

    _scheduleReconnect() {
        if (this._closed || this._reconnectTimer) return;
        const delay = this._reconnectDelay;
        this._reconnectDelay = Math.min(this._reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            this._connect();
        }, delay);
    }

    addEventListener(type, handler) {
        if (typeof handler !== 'function') return;
        let set = this._listeners.get(type);
        if (!set) {
            set = new Set();
            this._listeners.set(type, set);
        }
        set.add(handler);
        if (this._es) this._es.addEventListener(type, handler);
    }

    removeEventListener(type, handler) {
        const set = this._listeners.get(type);
        if (set) set.delete(handler);
        if (this._es) this._es.removeEventListener(type, handler);
    }

    get onopen() { return this._onopen; }
    set onopen(fn) {
        this._onopen = typeof fn === 'function' ? fn : null;
        if (this._es) this._es.onopen = this._onopen;
    }

    get onmessage() { return this._onmessage; }
    set onmessage(fn) {
        this._onmessage = typeof fn === 'function' ? fn : null;
        if (this._es) this._es.onmessage = this._onmessage;
    }

    get onerror() { return this._onerror; }
    set onerror(fn) {
        this._onerror = typeof fn === 'function' ? fn : null;
    }

    get readyState() {
        if (this._closed) return CLOSED;
        return this._es ? this._es.readyState : CONNECTING;
    }

    get url() { return this._url; }

    close() {
        this._closed = true;
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._es) {
            try { this._es.close(); } catch (_) { /* noop */ }
            this._es = null;
        }
    }
}

export default ReconnectingEventSource;
